import React, { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { VideoViewCrop } from './types';
import { normalizeVideoViewCrop } from './types';

type VideoCropMediaKind = 'uploaded' | 'youtube';
export type VideoCropFitMode = 'contain' | 'cover' | 'fill';

interface VideoCropViewportProps {
  aspectRatio?: number | null;
  children: ReactNode;
  fitMode?: VideoCropFitMode;
  isMirrored: boolean;
  mediaKind: VideoCropMediaKind;
  viewCrop?: VideoViewCrop;
}

const formatCropNumber = (value: number): string => (
  Number.parseFloat(value.toFixed(6)).toString()
);

const formatCropPercent = (value: number): string => (
  `${formatCropNumber(value * 100)}%`
);

const getCropClipPath = (crop: VideoViewCrop): string => (
  `inset(${formatCropPercent(crop.y)} ${formatCropPercent(1 - crop.x - crop.width)} ${formatCropPercent(1 - crop.y - crop.height)} ${formatCropPercent(crop.x)})`
);

const isUsableAspectRatio = (aspectRatio?: number | null): aspectRatio is number => (
  Boolean(aspectRatio && Number.isFinite(aspectRatio) && aspectRatio > 0)
);

/* The player box never changes shape when a crop is applied: it always takes the source aspect ratio,
   exactly like the uncropped video, so switching a crop on or off does not move the stage around. */
export const getVideoCropViewportStyle = (
  aspectRatio?: number | null
): CSSProperties | undefined => {
  if (!aspectRatio || !Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    return undefined;
  }

  return {
    '--video-crop-aspect-ratio': formatCropNumber(aspectRatio),
    '--video-crop-source-aspect-ratio': formatCropNumber(aspectRatio)
  } as CSSProperties;
};

/* The crop plays exactly like a video file of its own shape dropped into a fixed-shape player, which
   is to say object-fit: contain. It is clipped down to the region of interest, scaled by the tighter
   of the two axes so the whole region fits and touches the box edges on its limiting axis, then
   centred — leaving symmetric black bars on the other axis when the crop's shape does not match the
   box's. Since a crop is always a sub-rectangle of the frame, the scale is never below 1: a wide-and-
   short crop just zooms less than a tight one.

   `boxHeightRatio` is the box's height divided by the media's height (the media always spans the
   box's width). It is 1 when the box has the source aspect ratio; in fullscreen on an upright phone
   the box is much taller than the media, so the crop is fitted to — and centred in — the whole box
   rather than to a source-shaped strip at its top, letting a tall crop grow to fill the screen. */
export const getVideoCropMediaStyle = (
  viewCrop?: VideoViewCrop,
  boxHeightRatio = 1
): CSSProperties => {
  const normalizedCrop = normalizeVideoViewCrop(viewCrop);

  if (!normalizedCrop) {
    return { transform: 'none' };
  }

  // Never below 1: the box always shows at least the full-width media strip.
  const heightRatio = Number.isFinite(boxHeightRatio) ? Math.max(1, boxHeightRatio) : 1;
  const scale = Math.min(1 / normalizedCrop.width, heightRatio / normalizedCrop.height);
  const translateX = (0.5 / scale) - normalizedCrop.x - (normalizedCrop.width / 2);
  const translateY = (0.5 * heightRatio / scale) - normalizedCrop.y - (normalizedCrop.height / 2);

  return {
    clipPath: getCropClipPath(normalizedCrop),
    // CSS applies the rightmost transform first, so translate in source coordinates before zooming.
    transform: `scale(${formatCropNumber(scale)}) translate(${formatCropPercent(translateX)}, ${formatCropPercent(translateY)})`,
    transformOrigin: 'top left'
  };
};

/* Box height ÷ media height, where the media spans the box width at the source aspect ratio. Only
   measured while a crop is applied; 1 (the source-shaped box) otherwise or before layout. */
const useBoxHeightRatio = (
  viewportRef: React.RefObject<HTMLDivElement | null>,
  aspectRatio: number | null
): number => {
  const [ratio, setRatio] = useState(1);

  useLayoutEffect(() => {
    const element = viewportRef.current;
    if (!element || !aspectRatio) {
      setRatio(1);
      return;
    }

    const measure = () => {
      const { width, height } = element.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      const nextRatio = (height * aspectRatio) / width;
      // Ignore sub-pixel jitter so resizes do not re-render for nothing.
      setRatio(previous => (Math.abs(previous - nextRatio) < 0.001 ? previous : nextRatio));
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [viewportRef, aspectRatio]);

  return ratio;
};

const VideoCropViewport: React.FC<VideoCropViewportProps> = ({
  aspectRatio,
  children,
  fitMode = 'contain',
  isMirrored,
  mediaKind,
  viewCrop
}) => {
  const normalizedCrop = normalizeVideoViewCrop(viewCrop);
  const viewportRef = useRef<HTMLDivElement>(null);
  const boxHeightRatio = useBoxHeightRatio(
    viewportRef,
    normalizedCrop && isUsableAspectRatio(aspectRatio) ? aspectRatio : null
  );
  const viewportClassName = [
    'video-crop-viewport',
    `video-crop-viewport-${mediaKind}`,
    `video-crop-fit-${fitMode}`,
    isUsableAspectRatio(aspectRatio) ? 'has-known-aspect-ratio' : 'has-unknown-aspect-ratio',
    normalizedCrop ? 'is-cropped' : 'is-full-frame'
  ].join(' ');
  const mediaClassName = [
    'video-crop-media',
    `video-crop-media-${mediaKind}`
  ].join(' ');

  return (
    <div
      ref={viewportRef}
      className={viewportClassName}
      data-testid={`${mediaKind}-crop-viewport`}
      data-crop-active={normalizedCrop ? 'true' : 'false'}
      data-fit-mode={fitMode}
      style={getVideoCropViewportStyle(aspectRatio)}
    >
      <div
        className={`video-crop-mirror ${isMirrored ? 'is-mirrored' : ''}`}
        data-testid={`${mediaKind}-crop-mirror`}
      >
        <div
          className={mediaClassName}
          data-testid={`${mediaKind}-crop-media`}
          style={getVideoCropMediaStyle(normalizedCrop, boxHeightRatio)}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default VideoCropViewport;
